import InfoCircle from "@untitled-ui/icons-react/build/esm/InfoCircle";
import styled from "styled-components";
import Close from "../../../../assets/icons/x-close.svg";
import Logo from "../../../../assets/images/logo.png";
import noImage from "../../../../assets/images/no-image.png";
import zapFast2 from "../../../../assets/images/zap-fast2.png";
import { formatDefaultDateTime } from "../../../../utils/Formatter";
import BtnLargeSolid from "../../../common/buttons/BtnLargeSolid";
import CheckboxVector from "../../../common/checkbox/CheckboxVector";
import ReorderSuggestionTable, { AVAILABILITY_KEYS } from "./ReorderSuggestionTable";
import { useEffect, useState } from "react";
import ReorderSupplyBreakdown from "./ReorderSupplyBreakdown";
import * as Library from "../../../../utils/Library";
import FullScreenLoader from "../../../common/loaders/FullScreenLoader";

// #region SCOPED CSS
const StyledReorderSuggestionPopup = styled.div`
    .reorder-popup,
    .reorder-popup * {
        box-sizing: border-box;
    }

    .reorder-popup {
        --popup-background: var(--backgrounds-and-lines-n-400, #1e2631);
        --popup-surface: var(--backgrounds-and-lines-n-500, #141922);
        --popup-line: var(--backgrounds-and-lines-n-200, #262e3e);
        --popup-text: var(--text-and-icons-gray-100, #d1d1d1);
        --popup-green: var(--text-and-icons-green-500, #00bb00);
        --popup-title-accent: var(--ai-popup-title, #7763fe);
        background: var(--popup-background);
        border-radius: 4px;
        box-shadow: var(--drop-shadow-200-box-shadow, 0 0 48.1px 0 rgba(17, 22, 32, 0.51));
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-items: center;
        justify-content: flex-start;
        width: min(835px, calc(100vw - 48px));
        overflow: visible;
        padding: 56px 24px 32px;
        position: relative;
    }

    .popup-logo {
        border-radius: 9999px;
        width: 72px;
        height: 72px;
        position: absolute;
        left: 50%;
        top: -30px;
        transform: translateX(-50%);
    }

    .close-button {
        background: transparent;
        border: 0;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        position: absolute;
        right: 7px;
        top: 4px;
    }

    .close-button img {
        width: 16px;
        height: 16px;
    }

    .popup-header,
    .popup-body,
    .intro-section,
    .title-block,
    .table-section,
    .decision-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .popup-header {
        gap: 16px;
    }

    .popup-kicker {
        color: var(--popup-title-accent);
        font-size: 12px;
        font-weight: 500;
        line-height: normal;
        text-align: center;
        text-transform: uppercase;
    }

    .header-divider {
        background: var(--popup-line);
        height: 1px;
        width: 100%;
    }

    .popup-body {
        gap: 24px;
    }

    .intro-section {
        gap: 16px;
    }

    .created-date {
        align-self: flex-end;
        color: var(--popup-text);
        font-size: 12px;
        font-weight: 400;
        text-align: right;
    }

    .title-block {
        gap: 6px;
    }

    .title-row {
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-items: center;
        justify-content: center;
        width: 100%;
    }

    .title-icon {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
    }

    .popup-title {
        color: #ffffff;
        font-size: 24px;
        font-weight: 700;
        line-height: normal;
        margin: 0;
        text-align: center;
    }

    .popup-description {
        color: #ffffff;
        font-size: 14px;
        font-weight: 400;
        line-height: normal;
        margin: 0;
        text-align: center;
    }

    .product-summary {
        background: var(--popup-surface);
        border-radius: 10px;
        box-shadow: 0 4px 34px 0 rgba(25, 31, 38, 1);
        display: flex;
        flex-direction: row;
        gap: 16px;
        align-items: center;
        justify-content: center;
        padding: 6px 10px;
    }

    .product-image {
        border-radius: 4px;
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        object-fit: cover;
    }

    .product-details {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
        color: var(--popup-text);
        font-size: 14px;
        font-weight: 400;
    }

    .product-name,
    .product-reference {
        color: var(--popup-text);
    }

    .table-section {
        gap: 7px;
    }

    .helper-text {
        color: var(--popup-text);
        font-size: 12px;
        font-weight: 400;
        line-height: normal;
        margin: 0;
        text-align: center;
    }

    .table-scroll {
        width: 100%;
        overflow-x: auto;
    }

    .table-scroll > * {
        margin: 0 auto;
    }

    .decision-section {
        gap: 16px;
    }

    .transfer-actions {
        display: flex;
        flex-direction: row;
        gap: 16px;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
    }

    .transfer-notice {
        background: rgba(0, 187, 0, 0.09);
        border-radius: 4px;
        color: var(--popup-green);
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
        justify-content: center;
        min-height: 34px;
        padding: 0 10px;
    }

    .notice-icon {
        flex-shrink: 0;
        width: 12px;
        height: 12px;
        color: var(--popup-green);
    }

    .notice-text {
        color: var(--popup-green);
        font-size: 12px;
        font-weight: 400;
        line-height: normal;
        text-align: center;
    }

    .request-options {
        display: flex;
        flex-direction: row;
        gap: 26px;
        align-items: center;
        justify-content: center;
        min-height: 39px;
        width: 100%;
    }

    .request-option {
        color: var(--popup-text);
        display: flex;
        flex-direction: row;
        gap: 6px;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 400;
    }

    .footer-actions {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
    }
`;
// #endregion

// #region GLOBALS
const ALLOW_INTERNAL_TRANSFERS = false;
const ACTION_BUTTONS = ["DECLINE", "SEND"];
// #endregion

export default function ReorderSuggestionPopup({ handleClosePopup, data, reordersPageRefresh }) {
    // #region VARS
    const [showReorderSupplyBreakdown, setShowReorderSupplyBreakdown] = useState(false);
    const [transferInternally, setTransferInternally] = useState(false);
    const [requestToSupplier, setRequestToSupplier] = useState(false);
    const [hasSupplierStockAccess, setHasSupplierStockAccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const [productDetails, setProductDetails] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [tableHeaders, setTableHeaders] = useState({
        rows: [],
        columns: []
    });
    // #endregion

    // #region UI METHODS
    function handleImageFallback(event) {
        event.currentTarget.onerror = null;
        event.currentTarget.src = noImage;
    } //Handles the fallback image

    function handleButtonClick(action) {
        switch (action) {
            case "CHECK":
                setShowReorderSupplyBreakdown(true);
                break;
            case "DECLINE":
                updateReorderRequest(true);
                break;
            case "SEND":
                updateReorderRequest(false);
                break;
            default:
                break;
        }
    } //Handles the button click events
    // #endregion

    // #region DATA METHODS
    const compileProductDetails = () => {
        const product = data?.productData || {};
        const details = {
            imageUrl: product?.image?.URL,
            name: product?.itemName,
            reference: product?.reference
        };

        setProductDetails(details);
    }; //Compiles product details for the popup header

    const compileTableData = () => {
        const expandedItems = data?.expandedItems || [];
        const headers = {
            rows: data?.colors?.map(item => item?.colorName) || [],
            columns: data?.sizes || []
        };
        setTableHeaders(headers);

        /* Example of a rowData item:
            {
                "colorName": "Cocoa",
                "colorHexacode": "CCCCCC",
                "sizes": ["One size"],
                "variantsData": [
                    {
                        "barcode": "5714196155615",
                        "colorName": "Cocoa",
                        "size": "One size",
                        "stockAtDate": 0,
                        "leadTimeDays": "-",
                        "safetyStockDays": "-",
                        "targetCover": "-",
                        "lifecyclePercentage": "-",
                        "isUrgent": false,
                        "provisionalQuantityNeeded": 0,
                        "totalInternalTransfers": 0,
                        "supplierStock": 0,
                        "supplierAvailability": "None"
                    }
                ],
                "stockAtDate": [0],
                "recommendedQuantity": [0],
                "totalItems": 0
            }
            */

        try {
            // hasSupplierStockAccess is false ONLY if ALL supplierAvailability are null.
            // If at least one variant has supplierAvailability != null, it evaluates to true.
            const hasSupplierStockAccess =
                expandedItems?.some(item =>
                    item?.variantsData?.some(variant => variant?.supplierAvailability != null)
                ) ?? false;

            setHasSupplierStockAccess(hasSupplierStockAccess);

            const tableData = headers?.rows?.map(rowName => {
                const rowData = expandedItems?.find(item => item?.colorName?.toLowerCase() === rowName?.toLowerCase());
                if (!rowData) throw new Error("Row data not found");

                return rowData?.variantsData?.map(variant => {
                    let availability = AVAILABILITY_KEYS.UNKNOWN;

                    if (variant?.supplierAvailability === "None") availability = AVAILABILITY_KEYS.NOT_AVAILABLE;
                    else if (variant?.supplierAvailability === "Partial")
                        availability = AVAILABILITY_KEYS.PARTIALLY_AVAILABLE;
                    else if (variant?.supplierAvailability === "Full") availability = AVAILABILITY_KEYS.AVAILABLE;

                    return {
                        userModified: false,
                        provisionalQuantityNeeded: variant?.provisionalQuantityNeeded,
                        quantity: variant?.provisionalQuantityNeeded,
                        availability: availability,
                        color: variant?.colorName,
                        size: variant?.size,
                        urgent: variant?.isUrgent,
                        barcode: variant?.barcode,
                        hasQuantityDetails: variant?.provisionalQuantityNeeded > 0,
                        popupData: {
                            variation: `${variant?.colorName} / ${variant?.size}`,
                            suggestedQuantity: variant?.provisionalQuantityNeeded,
                            salesSpeed: variant?.sellthroughRate,
                            currentStock: variant?.stockAtDate,
                            targetCover: variant?.targetCover,
                            leadTimeDays: variant?.leadTimeDays,
                            safetyStockDays: variant?.safetyStockDays,
                            lifecyclePercentage: variant?.lifecyclePercentage,
                            lastUpdateDate: data?.orderData?.date
                        }
                    };
                });
            });

            setTableData(tableData);
        } catch (error) {
            console.error("Error compiling table data:", error);
            Library.showToastMessage({
                type: "error",
                message: "Error while compiling the reorder suggestion data, please try again later."
            });
            handleClosePopup();
            return;
        }
    }; //Compiles data for the table

    const updateReorderRequest = async isDeclined => {
        const supplierOrderID = data?.orderData?._id;
        let itemsPayload = [];
        const status = isDeclined ? "DeclinedByRetailBrand" : "Shared";

        if (!isDeclined) {
            itemsPayload = tableData?.reduce((acc, row) => {
                row?.forEach(item => {
                    if (item?.provisionalQuantityNeeded > 0 || item?.userModified) {
                        acc.push({
                            GTINbarcode: item?.barcode,
                            editedQuantityByRetailBrand: item?.quantity
                        });
                    }
                });
                return acc;
            }, []);
        }

        const payload = {
            supplierOrderID: supplierOrderID,
            status: status,
            items: itemsPayload
        };

        const res = await Library.makeApiRequest({
            endpoint: "SupplierOrders/updateReorder",
            data: payload,
            setLoader: setLoading
        });
        if (res?.data?.status !== 200) return;

        Library.showToastMessage({ type: "success", message: "Reorder suggestion sent successfully" });
        handleClosePopup();
        reordersPageRefresh();
    }; //Makes the request to update the reorder suggestion

    // #endregion

    // #region EFFECTS
    useEffect(() => {
        if (!data) return;

        compileProductDetails();
        compileTableData();
    }, [data]); // Compiles data needed for this popup
    // #endregion

    // #region COMPUTED VALUES
    const createdDate = formatDefaultDateTime(data?.orderData?.date, "--");
    // #endregion

    // #region RENDER
    return (
        <>
            <FullScreenLoader isLoading={loading} zIndex={100001} />

            {showReorderSupplyBreakdown && (
                <ReorderSupplyBreakdown handleClosePopup={() => setShowReorderSupplyBreakdown(false)} data={data} />
            )}
            <StyledReorderSuggestionPopup
                style={{
                    display: showReorderSupplyBreakdown ? "none" : "flex"
                }}>
                <div className="reorder-popup" role="dialog" aria-modal="true" aria-labelledby="reorder-popup-title">
                    <img className="popup-logo" src={Logo} alt="BOtag" />
                    <button
                        className="close-button"
                        type="button"
                        onClick={handleClosePopup}
                        aria-label="Close reorder suggestion">
                        <img src={Close} alt="" />
                    </button>

                    <header className="popup-header">
                        <div className="popup-kicker">REORDER SUGGESTION</div>
                        <div className="header-divider"></div>
                    </header>

                    <main className="popup-body">
                        <section className="intro-section">
                            <div className="created-date">Created on: {createdDate}</div>

                            <div className="title-block">
                                <div className="title-row">
                                    <img className="title-icon" src={zapFast2} alt="" />
                                    <h2 className="popup-title" id="reorder-popup-title">
                                        One or more variants of this item are running fast!
                                    </h2>
                                </div>
                                <p className="popup-description">
                                    To ensure you keep doing good business, BOtag AI suggests the following reorder
                                </p>
                            </div>

                            <div className="product-summary" aria-label="Suggested product">
                                <img
                                    className="product-image"
                                    src={productDetails?.imageUrl}
                                    onError={handleImageFallback}
                                    alt=""
                                />
                                <div className="product-details">
                                    <span className="product-name">{productDetails?.name ?? "--"}</span>
                                    <span className="product-reference">{productDetails?.reference ?? "--"}</span>
                                </div>
                            </div>
                        </section>

                        <section className="table-section">
                            <p className="helper-text">
                                Suggested quantities are based on this item's restock score. You can edit to adjust.
                                Hover &#9432; for details.
                            </p>
                            <div className="table-scroll">
                                <ReorderSuggestionTable
                                    tableHeaders={tableHeaders}
                                    tableData={tableData}
                                    setTableData={setTableData}
                                    hasSupplierStockAccess={hasSupplierStockAccess}
                                />
                            </div>
                        </section>

                        <section className="decision-section">
                            {ALLOW_INTERNAL_TRANSFERS && (
                                <div className="transfer-actions">
                                    <div className="transfer-notice">
                                        <InfoCircle className="notice-icon" />
                                        <span className="notice-text">
                                            Internal transfer from sibling stores is available
                                        </span>
                                    </div>
                                    <PopupButton text="CHECK" width="84px" onClick={() => handleButtonClick("CHECK")} />
                                </div>
                            )}

                            <div className="request-options">
                                {ALLOW_INTERNAL_TRANSFERS && (
                                    <RequestOption
                                        label="Transfer internally"
                                        isChecked={transferInternally}
                                        setOnCheck={setTransferInternally}
                                    />
                                )}
                                <RequestOption
                                    label="Request to supplier"
                                    isChecked={requestToSupplier}
                                    setOnCheck={setRequestToSupplier}
                                />
                            </div>
                        </section>
                    </main>

                    <footer className="footer-actions">
                        {ACTION_BUTTONS.map(buttonText => (
                            <PopupButton
                                key={buttonText}
                                text={buttonText}
                                disabled={buttonText === "SEND" && !requestToSupplier}
                                width="150px"
                                onClick={() => handleButtonClick(buttonText)}
                            />
                        ))}
                    </footer>
                </div>
            </StyledReorderSuggestionPopup>
        </>
    );
    // #endregion
}

// #region SMALL COMPONENTS
function PopupButton({ text, width, onClick, disabled }) {
    const POPUP_BUTTON_TEXT_STYLE = { fontSize: "12px", textTransform: "none" };

    const POPUP_BUTTON_COLORS = {
        normalColor: "var(--Buttons-All-Platforms-Gray-500, #10141b)",
        hoverColor: "var(--Buttons-All-Platforms-Gray-500, #10141b)",
        activeColor: "var(--Buttons-All-Platforms-Gray-500, #10141b)",
        disabledColor: "var(--Buttons-All-Platforms-Gray-500, #10141b)"
    };

    return (
        <BtnLargeSolid
            text={text}
            {...POPUP_BUTTON_COLORS}
            textStyle={POPUP_BUTTON_TEXT_STYLE}
            style={{ width }}
            onClick={onClick}
            disabled={disabled}
        />
    );
}

function RequestOption({ label, isChecked, setOnCheck }) {
    return (
        <div className="request-option cursor-pointer" onClick={() => setOnCheck(!isChecked)}>
            <CheckboxVector disabled={false} isChecked={isChecked} useGreenColorVariant={true} />
            <span>{label}</span>
        </div>
    );
}
// #endregion
